import React, { useEffect, useState } from "react"
import { Card, Button, Form, Row, Col, Spinner, Alert } from "react-bootstrap"
import { useNavigate, useParams } from "react-router-dom"
import Layout from "../../../layout/default"
import Block from "../../../components/Block/Block"
import { Icon } from "../../../components"
import subscriptionService from "../services/subscriptionService"
import planService from "../services/planService"
import billingCycleService from "../services/billingCycleService"
import api from "../../../utils/axiosInstance" // lawyer & payment-method mini-APIs
import Select from "react-select" // import react-select
import { registerLocale } from "react-datepicker"
import es from "date-fns/locale/es"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { Calendar } from "react-bootstrap-icons"
import { InputGroup } from "react-bootstrap"

registerLocale("es", es)
/* ────────────────────────────────────────────────────────────────
 *  Estado inicial
 * ──────────────────────────────────────────────────────────────── */
const emptySub = {
  lawyer: "",
  plan: "",
  cycle: "",
  payment_method: "",
  started_at: "", // ← NUEVO (yyyy-mm-dd)
  discount_percent: 0,
  vat_rate: 0.21,
  is_active: true,
}

const SubscriptionEdit = () => {
  /* ───────── routing ────────────────────────────── */
  const { id: rawId } = useParams()
  const isNew = !rawId || rawId === "new"
  const subId = isNew ? null : rawId
  const navigate = useNavigate()

  /* ───────── estado principal ───────────────────── */
  const [item, setItem] = useState(emptySub)
  const [loading, setLoading] = useState(Boolean(subId))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  /* ───────── catálogos ──────────────────────────── */
  const [users, setUsers] = useState([])
  const [plans, setPlans] = useState([])
  const [cycles, setCycles] = useState([])
  const [methods, setMethods] = useState([])

  /* ────────────────────────────────────────────────
   *  Carga inicial
   * ──────────────────────────────────────────────── */
  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        setLoading(true)
        /* ── 1. Traemos catálogos ───────────────────────────── */
        const [rawUsers, rawPlans, rawCycles, rawMethods] = await Promise.all([
          api.get("/lawyer/real-lawyer/").then((r) => r.data),
          planService.list(),
          billingCycleService.list(),
          api.get("/payment-methods/").then((r) => r.data),
        ])

        const toArr = (r) => (Array.isArray(r) ? r : r.results || r.data || [])
        const users = toArr(rawUsers)
        const plans = toArr(rawPlans)
        const cycles = toArr(rawCycles)
        const methods = toArr(rawMethods)

        if (!mounted) return
        setUsers(users)
        setPlans(plans)
        setCycles(cycles)
        setMethods(methods)

        /* ── 2. Si estamos editando, traemos la suscripción ─── */
        if (subId) {
          const rec = await subscriptionService.get(subId)
          if (!mounted) return

          /* ── 3. Map name/email → id ──────────────────────── */
          const findId = (list, key, value) =>
            list.find((el) => el[key] === value)?.id ?? ""

          setItem({
            lawyer: findId(users, "email", rec.lawyer_email),
            plan: findId(plans, "name", rec.plan_name),
            cycle: findId(cycles, "name", rec.cycle_name),
            payment_method: findId(methods, "name", rec.payment_method_name),
            started_at: rec.started_at?.slice(0, 10) || "",
            vat_rate: 0.21, // o rec.vat_rate si existiera
            discount_percent: 0,
            is_active: rec.is_active,
          })
        }
      } catch (err) {
        console.error(err)
        if (mounted) setError("Error al cargar datos de la suscripción.")
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [subId])

  /* ───────── handlers ───────────────────────────── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setItem((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const normalize = (rec) => ({
    ...rec,
    lawyer: rec.lawyer?.id ?? "",
    plan: rec.plan?.id ?? "",
    cycle: rec.cycle?.id ?? "",
    payment_method: rec.payment_method?.id ?? "",
    started_at: rec.started_at?.slice(0, 10) || "", // YYYY-MM-DD
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      if (isNew) await subscriptionService.create(item)
      else if (subId) await subscriptionService.update(subId, item)
      navigate("/subscription")
    } catch (err) {
      console.error("Error saving subscription", err)
      setError("No se pudo guardar la suscripción.")
    } finally {
      setSaving(false)
    }
  }

  /* ────────────────────────────────────────────────
   *  Opciones para react-select
   * ──────────────────────────────────────────────── */
  const lawyerOpts = users.map((u) => ({
    value: u.id,
    label: u.email,
  }))
  const planOpts = plans.map((p) => ({
    value: p.id,
    label: p.name,
  }))
  const cycleOpts = cycles.map((c) => ({
    value: c.id,
    label: c.name,
  }))
  const methodOpts = methods.map((m) => ({
    value: m.id,
    label: m.name,
  }))

  /* ────────────────────────────────────────────────
   *  Render
   * ──────────────────────────────────────────────── */
  return (
    <Layout
      title={isNew ? "Nueva Suscripción" : `Editar Suscripción #${subId}`}
      content="container"
      loading={loading}
    >
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">
              {isNew ? "Crear Suscripción" : `Editar Suscripción #${subId}`}
            </Block.Title>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block>
        <Card body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              {/* ─── Lawyer ───────────────────────*/}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Abogado</Form.Label>
                    <Select
                      options={lawyerOpts}
                      value={
                        lawyerOpts.find((o) => o.value === item.lawyer) || null
                      }
                      onChange={(option) =>
                        setItem((prev) => ({ ...prev, lawyer: option?.value }))
                      }
                      isClearable
                      placeholder="Buscar abogado…"
                    />
                  </Form.Group>
                </Col>
                {/* ─── Plan ─────────────────────────*/}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Plan</Form.Label>
                    <Select
                      options={planOpts}
                      value={
                        planOpts.find((o) => o.value === item.plan) || null
                      }
                      onChange={(option) =>
                        setItem((prev) => ({ ...prev, plan: option?.value }))
                      }
                      isClearable
                      placeholder="Buscar plan…"
                    />
                  </Form.Group>
                </Col>
                {/* ─── Cycle ────────────────────────*/}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Ciclo</Form.Label>
                    <Select
                      options={cycleOpts}
                      value={
                        cycleOpts.find((o) => o.value === item.cycle) || null
                      }
                      onChange={(option) =>
                        setItem((prev) => ({ ...prev, cycle: option?.value }))
                      }
                      isClearable
                      placeholder="Buscar ciclo…"
                    />
                  </Form.Group>
                </Col>
                {/* ─── Payment method ───────────────*/}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Método de Pago</Form.Label>
                    <Select
                      options={methodOpts}
                      value={
                        methodOpts.find(
                          (o) => o.value === item.payment_method
                        ) || null
                      }
                      onChange={(option) =>
                        setItem((prev) => ({
                          ...prev,
                          payment_method: option?.value,
                        }))
                      }
                      isClearable
                      placeholder="Buscar método…"
                    />
                  </Form.Group>
                </Col>
                {/* ─── Fecha inicio ─────────────────*/}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Fecha de inicio</Form.Label>

                    <InputGroup>
                      <DatePicker
                        selected={
                          item.started_at ? new Date(item.started_at) : null
                        }
                        onChange={(date) =>
                          setItem((p) => ({
                            ...p,
                            started_at: date
                              ? date.toISOString().slice(0, 10)
                              : "",
                          }))
                        }
                        locale="es"
                        dateFormat="dd/MM/yyyy"
                        placeholderText="dd/mm/aaaa"
                        className="form-control"
                        autoComplete="off"
                        popperPlacement="bottom-start"
                        popperClassName="datepicker-z" /* z-index alto */
                        closeOnScroll
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                {/* ─── VAT ──────────────────────────*/}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>IVA (0 – 1)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      name="vat_rate"
                      value={item.vat_rate}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                {/* ─── Activo ───────────────────────*/}
                <Col md={12} className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    label="Activo"
                    name="is_active"
                    checked={item.is_active}
                    onChange={handleChange}
                  />
                </Col>
                {/* ─── Botón guardar ────────────────*/}
                <Col xs={12} className="text-end">
                  <Button variant="primary" type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Guardando…
                      </>
                    ) : (
                      <>
                        <Icon name="save" />
                        <span> Guardar</span>
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          
        </Card>
      </Block>
    </Layout>
  )
}

export default SubscriptionEdit
